import React from 'react';
import styled, { css } from 'styled-components';
import { selectColor } from '@panel/commons';
import { useTranslation } from 'react-i18next';
import ChevronUp from '@panel/commons/assets/icons/chevronUpOrange.svg';
import ChevronUpCM from '@panel/commons/assets/icons/chevronUpCM.svg';
import ChevronUpVA from '@panel/commons/assets/icons/chevronUpVA.svg';
import ChevronUpUB from '@panel/commons/assets/icons/chevronUpUB.svg';
import ChevronDownOrange from '@panel/commons/assets/icons/chevronDownOrange.svg';
import ChevronDownCM from '@panel/commons/assets/icons/chevronDownCM.svg';
import ChevronDownVA from '@panel/commons/assets/icons/chevronDownVA.svg';
import ChevronDownUB from '@panel/commons/assets/icons/chevronDownUB.svg';
import ChevronDownGray from '@panel/commons/assets/icons/ChevronDown.svg';
import Button from './Button';
import ProductImg from '../../../assets/img/product-img.svg';
import errorIcon from '../../../assets/img/errorIcon.svg';

import AlertCircle from '../../../assets/img/alert-circle.svg';

const siteId = process.env.REACT_APP_BUILD_SITE;

function ProductsButton({ handleClick, isTableOpen, availableCount = '-', isLoading, isMobile = false, hasError }) {
  const { t } = useTranslation();
  const getBtnChildren = () => {
    if (isLoading) {
      return <LoadingSpan>{t('panel.available.products.title.loadingCredits')}</LoadingSpan>;
    }
    if (hasError && isMobile) {
      return (
        <HasErrorLoadingCredits>
          {t('panel.available.products.title.errorLoadingCredits.isMobile')}
        </HasErrorLoadingCredits>
      );
    }
    if (hasError) {
      return <HasErrorLoadingCredits>{t('panel.available.products.title.errorLoadingCredits')}</HasErrorLoadingCredits>;
    }
    return availableCount;
  };
  return (
    <Button
      data-panel='avisos_available_products'
      onClick={handleClick}
      isTableOpen={isTableOpen}
      bgColor={availableCount === 0 && !isLoading && '#FFEFF2'}
      bgColorHover={availableCount === 0 && !isLoading && '#F7E8EB'}
      isLoading={isLoading}
    >
      <ButtonContent isTableOpen={isTableOpen} isMobile={isMobile} data-tour-panel='products-button'>
        <div>
          <img
            src={!hasError ? ProductImg : errorIcon}
            alt='Icon'
            width={isMobile ? 16 : 26}
            height={isMobile ? 16 : 26}
          />
          {isMobile && isLoading ? (
            <LoadingSpan isMobile={isMobile}>{t('panel.available.products.title.loadingCredits.isMobile')}</LoadingSpan>
          ) : (
            <BtnText isMobile={isMobile}>
              {t('panel.available.products.title')}: {getBtnChildren()}
            </BtnText>
          )}
        </div>
        <div>
          {availableCount === 0 && !isLoading && <img src={AlertCircle} alt='' />}
          <BtnIcon isTableOpen={isTableOpen} isMobile={isMobile} isLoading={isLoading} />
        </div>
      </ButtonContent>
    </Button>
  );
}

const ButtonContent = styled.div`
  display: flex;
  padding: 8px 16px;
  height: ${({ isMobile }) => (isMobile ? 42 : 58)}px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
  }
`;

const BtnText = styled.div`
  font-family: Hind;
  font-size: ${({ isMobile }) => (isMobile ? 16 : 18)}px;
  font-weight: ${({ isMobile }) => (isMobile ? 500 : 600)};
  line-height: 23px;
  letter-spacing: 0em;
  color: ${selectColor('grayDarkLight')};
  ${({ isMobile }) =>
    isMobile &&
    css`
      text-overflow: clip;
    `}
`;
const HasErrorLoadingCredits = styled.span`
  color: ${selectColor('neutralMediumGray')};
  font-family: Hind;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
`;
const LoadingSpan = styled.span`
  font-family: Hind;
  font-size: ${({ isMobile }) => (isMobile ? 16 : 18)}px;
  font-weight: ${({ isMobile }) => (isMobile ? 500 : 600)};
  line-height: 23px;
  color: ${selectColor('midDarkGray')};
`;
const getChevronUpColor = () => {
  if (siteId === 'CMBR') return ChevronUpCM;
  if (siteId === 'UBPE') return ChevronUpUB;
  if (siteId === 'VAMX') return ChevronUpVA;
  return ChevronUp;
};

const getChevronDownColor = () => {
  if (siteId === 'CMBR') return ChevronDownCM;
  if (siteId === 'UBPE') return ChevronDownUB;
  if (siteId === 'VAMX') return ChevronDownVA;
  return ChevronDownOrange;
};

const BtnIcon = styled.div`
  width: 23px;
  height: 23px;
  margin-left: 12px;
  margin-right: 8px;
  background-image: ${({ isTableOpen, isMobile }) =>
    `url(${isTableOpen && !isMobile ? getChevronUpColor() : ChevronDownGray})`};
  :hover {
    background-image: ${({ isTableOpen, isMobile }) =>
      `url(${isTableOpen && !isMobile ? getChevronUpColor() : getChevronDownColor()})`};
  }
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  cursor: ${({ isLoading }) => (isLoading ? 'not-allowed' : 'pointer')};
  opacity: ${({ isLoading }) => (isLoading ? '0.5' : '1')};
  ${({ isMobile }) =>
    isMobile &&
    css`
      margin: 0;
      transform: rotate(-90deg);
    `}
`;

export default ProductsButton;
